import { z } from "zod";

/**
 * THE DELIVERY CONTRACT (PRD FR-7.1, ruling R-0910-3).
 *
 * `## Notifications` used to be read by a regex parser that had no vocabulary
 * for WEEKLY or REALTIME, so all three starter levels delivered the same daily
 * digest. The ruling's answer is that the section "should just do whatever it
 * says", and this file is the shape of "whatever it says": a strict schema a
 * model fills in, parsed here with the same fail-closed discipline as an
 * assessment — a reply that does not fit falls back to the DAILY default rather
 * than being guessed at.
 *
 * TWO INDEPENDENT PARTS, and that independence is the whole of rule 2. A digest
 * is a batch on a clock; an alert is an interrupt on a knock. WEEKLY is a digest
 * with no alerts, REALTIME is alerts with no digest, and DAILY is both — three
 * points in one space rather than three enum values with hard-coded behaviour.
 */

/** daily = every day · weekdays = Mon–Fri · weekly = one day a week. */
export const DIGEST_CADENCES = ["daily", "weekdays", "weekly"] as const;
export type DigestCadence = (typeof DIGEST_CADENCES)[number];

/** none = never interrupt · matching = only the rules below · every = every knock that passes. */
export const ALERT_MODES = ["none", "matching", "every"] as const;
export type AlertMode = (typeof ALERT_MODES)[number];

/**
 * The bands an alert rule may floor at, best first. They are the knock's own
 * `sender_band` percentile bands (ruling R-0927-1): a floor named in another
 * vocabulary (`TOP_20`, `LOW`) matched no band a knock is ever given, so a
 * "band at least" alert never fired on a `TOP_25` knock. The bottom 5% is not a
 * floor — a floor under everything is the `every` mode's job.
 */
export const ALERT_BANDS = ["TOP_5", "TOP_25", "MIDDLE", "BOTTOM_25"] as const;
export type AlertBand = (typeof ALERT_BANDS)[number];

/** Which knocks qualify for the digest: the N highest, or a percentile floor. */
export const NotificationThreshold = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("top_n"), n: z.number().int().min(1).max(25) }),
  z.object({ kind: z.literal("percentile"), min: z.number().int().min(1).max(100) }),
]);
export type NotificationThreshold = z.infer<typeof NotificationThreshold>;

/**
 * The batch. `weekday` is read only by the `weekly` cadence (0 = Sunday …
 * 6 = Saturday, the JS `getDay()` index the scheduler already speaks) and is
 * carried on every contract so the column never has to hold a null.
 */
export const NotificationDigest = z.object({
  enabled: z.boolean(),
  cadence: z.enum(DIGEST_CADENCES),
  weekday: z.number().int().min(0).max(6),
  hour: z.number().int().min(0).max(23),
  cap: z.number().int().min(1).max(25),
  threshold: NotificationThreshold,
  quiet_days: z.array(z.number().int().min(0).max(6)).max(7),
});
export type NotificationDigest = z.infer<typeof NotificationDigest>;

/**
 * One interrupt rule. `condition_text` is kept verbatim because the saved filter
 * is NAMED after what the owner actually wrote; the three fields under it are
 * the predicate, and a null field is a proven no-op (`recap/directives.ts`).
 */
export const NotificationAlertRule = z.object({
  condition_text: z.string().max(200),
  band_at_least: z.enum(ALERT_BANDS).nullable(),
  categories_any: z.array(z.string().max(80)).max(12).nullable(),
  followers_min: z.number().int().min(0).nullable(),
});
export type NotificationAlertRule = z.infer<typeof NotificationAlertRule>;

/** The interrupts. `rules` is read only by `matching` — the other two modes are absolute. */
export const NotificationAlerts = z.object({
  mode: z.enum(ALERT_MODES),
  rules: z.array(NotificationAlertRule).max(10),
});
export type NotificationAlerts = z.infer<typeof NotificationAlerts>;

export const NotificationContract = z.object({
  digest: NotificationDigest,
  alerts: NotificationAlerts,
});
export type NotificationContract = z.infer<typeof NotificationContract>;

/**
 * THE FR-7.1 DEFAULT, AND IT IS DAILY. Every road that does not end in a read
 * of the owner's own words ends here: a missing section, a Free door's prose, a
 * reply that did not parse, a plan with no room left. A function rather than a
 * constant because callers mutate what they are handed.
 */
export function defaultNotificationContract(): NotificationContract {
  return {
    digest: {
      enabled: true,
      cadence: "daily",
      weekday: 1,
      hour: 8,
      cap: 5,
      threshold: { kind: "top_n", n: 5 },
      quiet_days: [],
    },
    alerts: {
      mode: "matching",
      rules: [
        {
          condition_text: "high-priority matches",
          band_at_least: "TOP_5",
          categories_any: null,
          followers_min: null,
        },
      ],
    },
  };
}

/**
 * THE READ'S REPLY, AS A JSON SCHEMA THE VENDOR ENFORCES — the same mechanism
 * and the same options as `finalAssessmentJsonSchema`, and for the same reason:
 * the model produces one object, so it is told the object's shape rather than
 * asked for it in prose. `reused: "inline"` and `cycles: "throw"` keep it free
 * of `$defs`/`$ref`, which a strict `json_schema` rejects.
 */
export const NOTIFICATION_CONTRACT_SCHEMA_NAME = "notification_contract";
export const notificationContractJsonSchema: object = z.toJSONSchema(NotificationContract, {
  target: "draft-07",
  io: "output",
  reused: "inline",
  cycles: "throw",
});

export type NotificationContractParse =
  | { ok: true; contract: NotificationContract }
  | { ok: false; reason: "invalid_json" | "schema_mismatch" };

/**
 * Fail-closed, exactly as a trailer is: text in, a contract or a REASON out, and
 * never a half-read object with the missing halves guessed at. The caller turns
 * a failure into the default plus a notice.
 */
export function parseNotificationContract(text: string): NotificationContractParse {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return { ok: false, reason: "invalid_json" };
  }
  const parsed = NotificationContract.safeParse(value);
  if (!parsed.success) return { ok: false, reason: "schema_mismatch" };
  return { ok: true, contract: parsed.data };
}
